import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Chip
} from '@mui/material';

export default function ProcessingResultsDialog({ open, onClose, results, type }) {
    if (!results) return null;

    const getTitle = () => {
        switch(type) {
            case 'check': return 'Email Check Results';
            case 'process': return 'Email Processing Results';
            case 'followup': return 'Follow-up Results';
            case 'forwarded': return 'Forwarded Email Results';
            default: return 'Results';
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{getTitle()}</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6">
                        {type === 'check' && `Total Emails Found: ${results.total_emails_found || 0}`}
                        {type === 'process' && `Total Emails Sent: ${results.total_emails_sent || 0}`}
                        {type === 'followup' && `Total Follow-ups Sent: ${results.total_followups_sent || 0}`}
                        {type === 'forwarded' && `Messages Processed: ${results.messages_processed || 0}`}
                    </Typography>
                </Box>

                <List>
                    {results.results?.map((result, idx) => (
                        <ListItem key={idx}>
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body1">{result.username}</Typography>
                                        <Chip 
                                            label={result.status}
                                            color={result.status === 'success' ? 'success' : 'error'}
                                            size="small"
                                        />
                                    </Box>
                                }
                                secondary={
                                    <Box>
                                        {type === 'check' && (
                                            <>
                                                <Typography variant="caption">
                                                    Emails found: {result.emails_found || 0}
                                                </Typography>
                                                {result.classifications && (
                                                    <Box sx={{ mt: 1 }}>
                                                        {Object.entries(result.classifications).map(([cat, count]) => (
                                                            <Chip 
                                                                key={cat}
                                                                label={`${cat}: ${count}`}
                                                                size="small"
                                                                sx={{ mr: 0.5, mt: 0.5 }}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                        {type === 'process' && (
                                            <>
                                                <Typography variant="caption">
                                                    Emails sent: {result.emails_sent || 0} | 
                                                    Classified: {result.emails_classified || 0} | 
                                                    New contacts: {result.new_crm_contacts || 0}
                                                </Typography>
                                                {result.categories && (
                                                    <Box sx={{ mt: 1 }}>
                                                        {Object.entries(result.categories).map(([cat, count]) => (
                                                            <Chip 
                                                                key={cat}
                                                                label={`${cat}: ${count}`}
                                                                size="small"
                                                                sx={{ mr: 0.5, mt: 0.5 }}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                        {type === 'followup' && result.followup_types && (
                                            <Box sx={{ mt: 1 }}>
                                                {Object.entries(result.followup_types).map(([type, count]) => (
                                                    <Chip 
                                                        key={type}
                                                        label={`${type.replace(/_/g, ' ')}: ${count}`}
                                                        size="small"
                                                        sx={{ mr: 0.5, mt: 0.5 }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                        {result.error && (
                                            <Typography variant="caption" color="error">
                                                Error: {result.error}
                                            </Typography>
                                        )}
                                    </Box>
                                }
                            />
                        </ListItem>
                    ))}
                </List>

                {results.details && type === 'forwarded' && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2">Details:</Typography>
                        <List dense>
                            {results.details.slice(0, 10).map((detail, idx) => (
                                <ListItem key={idx}>
                                    <ListItemText
                                        primary={detail.status}
                                        secondary={detail.snippet || detail.reason || detail.error}
                                        secondaryTypographyProps={{ variant: 'caption' }}
                                    />
                                </ListItem>
                            ))}
                            {results.details.length > 10 && (
                                <Typography variant="caption" color="text.secondary">
                                    ... and {results.details.length - 10} more
                                </Typography>
                            )}
                        </List>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}